import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import {
  createStandard,
  updateStandard,
  getStandardDetail,
} from "../../helpers/backend_helper";
import { showLoading } from "react-global-loading";
import "./Standards.scss";

const StandardDetail = ({ detailId, closeAction }) => {
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    description: "",
  });
  const isEdit = Boolean(detailId);

  useEffect(() => {
    if (!detailId) return;
    showLoading(true);
    getStandardDetail(detailId)
      .then((response) => {
        if (response.status) {
          setFormData({
            id: response.data.id,
            name: response.data.name || "",
            description: response.data.description || "",
          });
        } else {
          ErrorNotify(response.message);
        }
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the standard details.");
      })
      .finally(() => showLoading(false));
  }, [detailId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Enter a standard name"),
    }),
    onSubmit: (values) => {
      const request = {
        name: values.name.trim(),
        description: values.description?.trim() || "",
      };
      if (values.id) request.id = values.id;
      showLoading(true);
      (values.id ? updateStandard(request) : createStandard(request))
        .then((response) => {
          if (response.status) {
            SuccessNotify(
              values.id
                ? "Standard updated successfully."
                : "Standard created successfully."
            );
            closeAction?.();
          } else {
            ErrorNotify(response.message);
          }
        })
        .catch((error) => {
          console.error(error);
          ErrorNotify("Unable to save the standard.");
        })
        .finally(() => showLoading(false));
    },
  });

  return (
    <Form
      className="standard-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {isEdit
            ? "Update the standard details used across inspection and review workflows."
            : "Add a standard that can be used across inspection and review workflows."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-book-open" />
              Standard Information
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="standard-name">
              Standard Name <b>*</b>
            </Label>
            <Input
              id="standard-name"
              name="name"
              placeholder="Enter standard name"
              value={validation.values.name}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.name && validation.errors.name
              )}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="standard-description">Description</Label>
            <Input
              id="standard-description"
              name="description"
              type="textarea"
              rows="5"
              maxLength="500"
              placeholder="Describe the purpose and scope of this standard"
              value={validation.values.description}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            <div className="standard-drawer-form__count">
              {validation.values.description.length} / 500
            </div>
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section standard-drawer-form__section--info">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-info-circle" />
              What happens next
            </span>
          </div>
          <div className="standard-drawer-form__next-steps">
            <strong>After saving, you can</strong>
            <span>
              <i className="bx bx-check" />
              Use this standard in inspection cycles
            </span>
            <span>
              <i className="bx bx-check" />
              Update its description at any time
            </span>
            <span>
              <i className="bx bx-check" />
              Manage it from the standards list
            </span>
          </div>
        </section>
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit">
          {isEdit ? "Save Changes" : "Create Standard"}
        </Button>
      </footer>
    </Form>
  );
};

export default StandardDetail;
